/**
 * Academics domain.
 *
 * Records academic marks: a single numeric score per learner per assessment.
 * Teachers see the domain labelled "Academics"; "Marks" is what you enter into
 * it. It sits alongside Discipline, Recognition, Attendance, Participation, and
 * Health as a peer on the same shared rules engine — a config, not a bespoke
 * service.
 *
 * What's different from every other domain: an academics event carries a
 * numeric `score` (written by the engine as `numericValue` + `maxScore`)
 * instead of a picked category. The numeric input surface already exists — a
 * `number`-typed `primary` makes Quick Issue render a score column and an
 * "Out of" header (see `isNumberFieldType` in domainHelpers + the number-mode
 * branch in QuickIssueDashboardRefactored / ClassListViewSection). So this
 * config alone is enough to start recording marks; no input code changes.
 *
 * Phased build (see knowledge/wiki/features/marks-domain.md):
 *   - Phase 1 (this file): record a score + optional comment.
 *   - Phase 2: stamp `subjectId` (from the class) + `assessmentName` onto events.
 *   - Phase 3: `score-aggregation` rules (avg/count/streak over scores, scoped
 *     by subject + term) and the at-risk-vs-achievement valency split. That
 *     split is a downstream classification on RULES, NOT a second input — there
 *     is one mark sheet, two kinds of output.
 *
 * Schema shape (validated by `assertDomainShape` on import — a bad shape fails
 * the build):
 *   primary  — the singular headline field: the score. One Quick Issue column.
 *   followUp — an optional free-text comment opened after the score is entered.
 *
 * Universal fields (recordedBy, occurredAt, state) are NOT declared here — the
 * engine writes them on every event.
 */

import { assertDomainShape } from '../schemaAssert.js';
import { buildDomainCapabilities, buildDomainSurfaces } from '../centralModel.js';

export const ACADEMICS_DOMAIN_ID = 'academics';

const PRIMARY = {
  id: 'score',
  label: 'Score',
  type: 'number',
};

const FOLLOW_UP = [
  { id: 'comment', label: 'Comment', type: 'text', optional: true },
];

const TERMINOLOGY = {
  event:    { label: 'mark',          plural: 'marks',          description: 'A single recorded score for a learner' },
  category: { label: 'subject',       plural: 'subjects',       description: 'The subject a mark belongs to' },
  type:     { label: 'assessment',    plural: 'assessments',    description: 'Test, quiz, exam, assignment' },
  action:   { label: 'follow-up',     plural: 'follow-ups',     description: 'What rules trigger after a mark is recorded' },
  rule:     { label: 'rule',          plural: 'rules',          description: '' },
  queue:    { label: 'academics queue', plural: 'academics queues', description: '' },
};

export const academicsDomainConfig = assertDomainShape({
  name: 'academics',
  label: 'Academics',
  description: 'Academic marks — scores recorded per learner per assessment',
  protected: false,
  active: true,
  sortOrder: 8,
  icon: null,
  color: 'primary',
  // Marks are entered list-style across a class, like a mark sheet.
  defaultInputMethod: 'list',
  defaultView: 'list',
  // Default ceiling for a score; a session can override via the "Out of" header.
  maxScore: 100,
  collections: {
    events: 'academicsEvents',
    rules: 'academicsRules',
    types: 'academicsTypes',
    actions: 'academicsActions',
    queue: 'academicsQueue',
  },
  eventsCollection: 'academicsEvents',
  rulesCollection: 'academicsRules',
  capabilities: buildDomainCapabilities({
    recording: {
      enabled: true,
      context: 'assessment',
      supportedContexts: ['assessment'],
    },
    collective: {
      enabled: true,
      aggregates: ['count', 'breakdown', 'trend', 'baseline'],
      audiences: ['staff', 'parent-summary'],
    },
    rules: { enabled: true, categories: ['score-aggregation'] },
    actions: { enabled: true, outcomes: ['follow-up'] },
  }),
  surfaces: buildDomainSurfaces({ rules: true, dashboard: true, reports: true }),
  primary: PRIMARY,
  followUp: FOLLOW_UP,
  terminology: TERMINOLOGY,
  contextTags: {},
  scope: { grades: [] },
});

export default academicsDomainConfig;
